<template>
  <div>
    <template v-if="recurrencies.length">
      <div class="row">
        <div
          v-for="recurrency in recurrencies"
          :key="recurrency.id"
          class="col-12 col-md-6 col-xl-4 mb-3"
        >
          <div class="recurrency-card">
            <div class="recurrency-header">
              <div class="recurrency-avatar recurrency-avatar-placeholder">
                <i class="fa-solid fa-user"></i>
              </div>
              <div class="recurrency-header-info">
                <router-link
                  v-if="recurrency.user?.id"
                  :to="`${$paths.users.detail}/${recurrency.user.id}`"
                  class="recurrency-name text-truncate recurrency-name-link"
                >
                  {{ recurrency.user.name }}
                </router-link>
                <div v-else class="recurrency-name text-truncate">
                  {{ recurrency.user?.name || "Paciente" }}
                </div>
                <div class="recurrency-price">R$ {{ recurrency.value }}</div>
              </div>
              <span
                class="recurrency-status-badge"
                :class="{
                  'status-pending': recurrency.status === 'pending',
                  'status-active': recurrency.status === 'active',
                  'status-cancelled': !['pending', 'active'].includes(recurrency.status),
                }"
              >
                <i
                  class="fa-solid me-1"
                  :class="{
                    'fa-circle-exclamation': recurrency.status === 'pending',
                    'fa-circle-check': recurrency.status === 'active',
                    'fa-circle-xmark': !['pending', 'active'].includes(recurrency.status),
                  }"
                ></i>
                <span v-if="recurrency.status === 'pending'">Pendente</span>
                <span v-else-if="recurrency.status === 'active'">Ativa</span>
                <span v-else>Cancelada</span>
              </span>
            </div>

            <div class="recurrency-body">
              <div class="recurrency-info-line">
                <i class="fa-solid fa-calendar-days me-2 text-muted"></i>
                <span>
                  <strong>{{ recurrencyFrequencyLabel(recurrency) }}</strong>
                  <span
                    v-if="recurrencyDayLabel(recurrency)"
                    class="text-muted"
                  >
                    · {{ recurrencyDayLabel(recurrency) }}
                  </span>
                </span>
              </div>
              <div
                v-if="hasPendingValueChange(recurrency)"
                class="recurrency-value-change"
              >
                <i class="fa-solid fa-arrow-trend-up me-2"></i>
                <span>
                  Mudança de valor agendada para
                  <strong>
                    {{ $moment(recurrency.value_change_effective_at).format("DD/MM/YYYY") }}
                  </strong>
                </span>
              </div>
              <div
                v-if="recurrency.created_at"
                class="recurrency-info-line text-muted"
              >
                <i class="fa-solid fa-clock me-2"></i>
                <small>
                  Recorrência criada em
                  {{ $moment(recurrency.created_at).format("DD/MM/YYYY") }}
                </small>
              </div>
            </div>

            <div class="recurrency-footer">
              <router-link
                :to="{
                  path: $paths.appointments.list,
                  query: { recurrency_id: recurrency.id },
                }"
                class="btn btn-outline-secondary btn-sm flex-fill"
              >
                <i class="fa-solid fa-calendar me-1"></i>
                Ver sessões
              </router-link>
              <template v-if="recurrency.status === 'active'">
                <button
                  class="btn btn-outline-primary btn-sm flex-fill"
                  @click="openEditRecurrencyModal(recurrency)"
                >
                  <i class="fa-solid fa-pen me-1"></i>
                  Editar
                </button>
                <button
                  class="btn btn-outline-danger btn-sm flex-fill"
                  :disabled="cancelingRecurrency === recurrency.id"
                  @click="handleCancelRecurrency(recurrency)"
                >
                  <span v-if="cancelingRecurrency === recurrency.id">
                    <i class="fa-solid fa-spinner fa-spin me-1"></i>
                    Cancelando...
                  </span>
                  <span v-else>
                    <i class="fa-solid fa-ban me-1"></i>
                    Cancelar
                  </span>
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div v-else class="text-center py-5 text-muted">
      <i class="fa-solid fa-repeat fa-3x mb-3 opacity-25"></i>
      <p class="mb-0">Nenhuma recorrência</p>
    </div>

    <!-- Modal: Editar recorrência -->
    <div
      v-if="showEditRecurrencyModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0,0,0,0.5)"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fa-solid fa-pen me-2 text-primary"></i>
              Editar recorrência
            </h5>
            <button type="button" class="btn-close" @click="closeEditRecurrencyModal"></button>
          </div>
          <div class="modal-body" v-if="editRecurrencyForm.recurrency">
            <div class="recurrency-summary mb-3">
              <div class="d-flex align-items-center gap-3">
                <div
                  class="recurrency-avatar recurrency-avatar-placeholder"
                  style="width: 48px; height: 48px"
                >
                  <i class="fa-solid fa-user"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-bold">
                    {{ editRecurrencyForm.recurrency.user?.name }}
                  </div>
                  <small class="text-muted">
                    {{ recurrencyFrequencyLabel(editRecurrencyForm.recurrency) }}
                    <span v-if="recurrencyDayLabel(editRecurrencyForm.recurrency)">
                      · {{ recurrencyDayLabel(editRecurrencyForm.recurrency) }}
                    </span>
                  </small>
                </div>
                <div class="text-end">
                  <small class="text-muted d-block">Valor atual</small>
                  <strong>R$ {{ editRecurrencyForm.recurrency.value }}</strong>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Novo valor (R$)</label>
              <input
                type="number"
                class="form-control"
                :class="{ 'is-invalid': editRecurrencyValueBelowMin }"
                v-model.number="editRecurrencyForm.value"
                min="50"
                step="0.01"
                placeholder="Ex: 150,00"
              />
              <div v-if="editRecurrencyValueBelowMin" class="invalid-feedback d-block">
                O valor mínimo permitido é R$ 50,00.
              </div>
            </div>

            <div
              v-if="editRecurrencyBigChange"
              class="alert alert-warning py-2 small mb-3"
            >
              <i class="fa-solid fa-triangle-exclamation me-1"></i>
              <strong>Atenção:</strong>
              o novo valor representa uma variação de
              <strong>{{ editRecurrencyChangePctFormatted }}</strong>
              em relação ao valor atual. Confirme se essa mudança está correta.
            </div>

            <div class="mb-3">
              <label class="form-label">Data de início da mudança de valor</label>
              <input
                type="date"
                class="form-control"
                v-model="editRecurrencyForm.effective_date"
                :min="today"
              />
              <small class="text-muted">
                O novo valor será aplicado a partir dessa data.
                Sessões já agendadas antes dessa data mantêm o valor atual.
              </small>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeEditRecurrencyModal">
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!canSubmitEditRecurrency || submittingEditRecurrency"
              @click="handleEditRecurrency"
            >
              <span v-if="submittingEditRecurrency">
                <i class="fa-solid fa-spinner fa-spin me-1"></i>
                Salvando...
              </span>
              <span v-else>
                <i class="fa-solid fa-check me-1"></i>
                Confirmar mudança
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PsiDetailRecurrencies",
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      cancelingRecurrency: null,
      showEditRecurrencyModal: false,
      submittingEditRecurrency: false,
      editRecurrencyForm: {
        recurrency: null,
        value: null,
        effective_date: "",
      },
    };
  },
  computed: {
    recurrencies() {
      return this.item?.recurrencies_as_psy || [];
    },
    today() {
      return this.$moment().format("YYYY-MM-DD");
    },
    canSubmitEditRecurrency() {
      return (
        this.editRecurrencyForm.value >= 50 &&
        !!this.editRecurrencyForm.effective_date
      );
    },
    editRecurrencyValueBelowMin() {
      return (
        this.editRecurrencyForm.value != null &&
        this.editRecurrencyForm.value < 50
      );
    },
    editRecurrencyChangePct() {
      const current = parseFloat(this.editRecurrencyForm.recurrency?.value);
      const next = parseFloat(this.editRecurrencyForm.value);
      if (!current || !next) return 0;
      return ((next - current) / current) * 100;
    },
    editRecurrencyBigChange() {
      return Math.abs(this.editRecurrencyChangePct) >= 20;
    },
    editRecurrencyChangePctFormatted() {
      const pct = this.editRecurrencyChangePct;
      const sign = pct > 0 ? "+" : "";
      return `${sign}${pct.toFixed(1)}%`;
    },
  },
  methods: {
    hasPendingValueChange(recurrency) {
      if (!recurrency?.value_change_effective_at) return false;
      return this.$moment(recurrency.value_change_effective_at).isAfter(
        this.$moment()
      );
    },
    recurrencyFrequencyLabel(recurrency) {
      if (!recurrency) return "";
      if (recurrency.frequency === "weekly" && recurrency.interval === 1) return "Semanal";
      if (recurrency.frequency === "weekly" && recurrency.interval === 2) return "Quinzenal";
      if (recurrency.frequency === "monthly" && recurrency.interval === 1) return "Mensal";
      return `${recurrency.frequency} (intervalo ${recurrency.interval})`;
    },
    recurrencyDayLabel(recurrency) {
      if (!recurrency?.reference_date) return "";
      const ref = this.$moment(recurrency.reference_date);
      if (!ref.isValid()) return "";
      const minutes = ref.minute();
      const time =
        minutes === 0
          ? `${ref.hour()}h`
          : `${ref.hour()}h${String(minutes).padStart(2, "0")}`;
      if (recurrency.frequency === "weekly") {
        const weekdays = [
          "domingo",
          "segunda-feira",
          "terça-feira",
          "quarta-feira",
          "quinta-feira",
          "sexta-feira",
          "sábado",
        ];
        return `${weekdays[ref.day()]} às ${time}`;
      }
      if (recurrency.frequency === "monthly") {
        return `dia ${ref.date()} às ${time}`;
      }
      return "";
    },
    openEditRecurrencyModal(recurrency) {
      this.editRecurrencyForm.recurrency = recurrency;
      this.editRecurrencyForm.value = parseFloat(recurrency.value) || null;
      this.editRecurrencyForm.effective_date = this.$moment()
        .add(2, "days")
        .format("YYYY-MM-DD");
      this.showEditRecurrencyModal = true;
    },
    closeEditRecurrencyModal() {
      this.showEditRecurrencyModal = false;
      this.editRecurrencyForm.recurrency = null;
      this.editRecurrencyForm.value = null;
      this.editRecurrencyForm.effective_date = "";
    },
    async handleEditRecurrency() {
      if (!this.editRecurrencyForm.recurrency) return;

      const current = this.editRecurrencyForm.recurrency.value;
      const next = this.editRecurrencyForm.value;
      const date = this.$moment(
        this.editRecurrencyForm.effective_date
      ).format("DD/MM/YYYY");

      let confirmMsg = `Confirmar alteração de valor?\n\nValor atual: R$ ${current}\nNovo valor: R$ ${next}\nA partir de: ${date}`;
      if (this.editRecurrencyBigChange) {
        confirmMsg += `\n\n⚠ ATENÇÃO: variação de ${this.editRecurrencyChangePctFormatted} em relação ao valor atual.`;
      }

      const ok = confirm(confirmMsg);
      if (!ok) return;

      this.submittingEditRecurrency = true;
      try {
        await this.$appointmentsService.network.put(
          `api/appointment-recurrencies/${this.editRecurrencyForm.recurrency.id}`,
          {
            value: this.editRecurrencyForm.value,
            value_effective_from: this.editRecurrencyForm.effective_date,
          }
        );
        this.$eventBus.$emit("showToast", {
          title: "Recorrência atualizada",
          msg: `Novo valor de R$ ${this.editRecurrencyForm.value} entrará em vigor a partir de ${date}.`,
        });
        this.closeEditRecurrencyModal();
        this.$emit("refresh");
      } catch (error) {
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: error.message || "Erro ao atualizar recorrência.",
        });
      } finally {
        this.submittingEditRecurrency = false;
      }
    },
    async handleCancelRecurrency(recurrency) {
      const patientName = recurrency.user?.name || "esse paciente";
      const ok = confirm(
        `Tem certeza que deseja cancelar esta recorrência com ${patientName}?\n\nSomente sessões futuras que NÃO foram pagas serão canceladas.`
      );
      if (!ok) return;
      this.cancelingRecurrency = recurrency.id;
      try {
        await this.$appointmentsService.cancelRecurrency(recurrency.id);
        this.$eventBus.$emit("showToast", {
          title: "Recorrência cancelada",
          msg: "Sessões futuras que não haviam sido pagas foram canceladas.",
        });
        this.$emit("refresh");
      } catch (error) {
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: error.message || "Erro ao cancelar recorrência.",
        });
      } finally {
        this.cancelingRecurrency = null;
      }
    },
  },
};
</script>

<style scoped>
.recurrency-card {
  background-color: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.15s;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.recurrency-card:hover {
  border-color: #dee2e6;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.recurrency-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #f1f3f5;
}

.recurrency-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background-color: #f8f9fa;
  display: block;
}

.recurrency-avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e9ecef;
  color: #adb5bd;
  font-size: 1.2rem;
}

.recurrency-header-info {
  flex: 1;
  overflow: hidden;
  min-width: 0;
}

.recurrency-name {
  font-weight: 600;
  color: #212529;
  font-size: 0.95rem;
  line-height: 1.2;
  display: block;
}

.recurrency-name-link {
  text-decoration: none;
  transition: color 0.15s;
}

.recurrency-name-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

.recurrency-price {
  font-size: 0.85rem;
  color: #495057;
  margin-top: 2px;
}

.recurrency-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}

.recurrency-status-badge.status-active {
  color: #0f5132;
  background-color: #d1e7dd;
}

.recurrency-status-badge.status-pending {
  color: #664d03;
  background-color: #fff3cd;
}

.recurrency-status-badge.status-cancelled {
  color: #842029;
  background-color: #f8d7da;
}

.recurrency-body {
  padding: 14px 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.recurrency-info-line {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #495057;
}

.recurrency-info-line small {
  font-size: 0.75rem;
}

.recurrency-value-change {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  padding: 8px 10px;
  background-color: #fff7ed;
  color: #92400e;
  border: 1px solid #fed7aa;
  border-radius: 6px;
}

.recurrency-footer {
  padding: 0 16px 14px 16px;
  display: flex;
  gap: 8px;
}

.recurrency-summary {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 12px 16px;
  border: 1px solid #e9ecef;
}
</style>
